// lib/api/device.ts
import axiosInstance from './axios';
import { API_ENDPOINTS } from './config';
import { ApiError } from './error';
import {
    DeviceCreateType,
    DeviceUpdateType,
    DeviceResponseType,
    DeviceDetailResponseType,
    DeviceInstallationResponseType,
    DevicePhotoResponseType,
    PageResponse,
    DeviceIdCheckResponseType,
    DeviceStatsResponseType,
    RegionResponseType
} from '@/types';

// API_BASE_URL import 추가
const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || 'http://localhost:8090';

/**
 * 이미지 URL 변환 헬퍼 함수
 */
export const getImageUrl = (filePath: string | null | undefined): string => {
    if (!filePath) return '';

    // 이미 완전한 URL인 경우
    if (filePath.startsWith('http://') || filePath.startsWith('https://')) {
        return filePath;
    }

    // 백엔드 서버의 파일 경로인 경우
    let cleanPath = filePath.replace(/\\/g, '/'); // 백슬래시를 슬래시로 변환

    // 상대 경로 처리 (./ 제거)
    if (cleanPath.startsWith('./')) {
        cleanPath = cleanPath.substring(2);
    }

    // uploads/ 접두사 제거
    if (cleanPath.startsWith('uploads/')) {
        cleanPath = cleanPath.substring(8); // 'uploads/' 제거
    }

    // 앞에 슬래시가 없으면 추가
    if (!cleanPath.startsWith('/')) {
        cleanPath = '/' + cleanPath;
    }

    // API_BASE_URL이 정의되지 않은 경우 기본값 사용
    const baseUrl = API_BASE_URL || 'http://localhost:8090';

    return `${baseUrl}/api/files${cleanPath}`;
};

/**
 * 기기 목록 조회
 */
export async function getDeviceListApi(
    page = 0,
    size = 10,
    filters?: {
        regionCode?: string;
        deviceName?: string;
        installationDate?: string;
        status?: string;
    }
): Promise<PageResponse<DeviceResponseType>> {
    try {
        const response = await axiosInstance.get<PageResponse<DeviceResponseType>>(
            API_ENDPOINTS.DEVICE.LIST,
            {
                params: { page, size, ...filters }
            }
        );
        return response;
    } catch (error) {
        throw ApiError.fromAxiosError(error);
    }
}

/**
 * 기기 검색
 */
export async function searchDevicesApi(
    keyword: string,
    page = 0,
    size = 10
): Promise<PageResponse<DeviceResponseType>> {
    try {
        const response = await axiosInstance.get<PageResponse<DeviceResponseType>>(
            API_ENDPOINTS.DEVICE.SEARCH,
            {
                params: { keyword, page, size }
            }
        );
        return response;
    } catch (error) {
        throw ApiError.fromAxiosError(error);
    }
}

/**
 * 기기 상세 조회
 */
export async function getDeviceDetailApi(deviceId: number): Promise<DeviceDetailResponseType> {
    try {
        const response = await axiosInstance.get<DeviceDetailResponseType>(
            API_ENDPOINTS.DEVICE.DETAIL(deviceId)
        );
        return response;
    } catch (error) {
        throw ApiError.fromAxiosError(error);
    }
}

/**
 * 기기 등록
 */
export async function createDeviceApi(deviceData: DeviceCreateType): Promise<DeviceResponseType> {
    try {
        const response = await axiosInstance.post<DeviceResponseType>(
            API_ENDPOINTS.DEVICE.CREATE,
            deviceData
        );
        return response;
    } catch (error) {
        throw ApiError.fromAxiosError(error);
    }
}

/**
 * 기기 수정
 */
export async function updateDeviceApi(
    deviceId: number,
    deviceData: DeviceUpdateType
): Promise<DeviceResponseType> {
    try {
        const response = await axiosInstance.put<DeviceResponseType>(
            API_ENDPOINTS.DEVICE.UPDATE(deviceId),
            deviceData
        );
        return response;
    } catch (error) {
        throw ApiError.fromAxiosError(error);
    }
}

/**
 * 기기 삭제
 */
export async function deleteDeviceApi(deviceId: number): Promise<void> {
    try {
        await axiosInstance.delete(API_ENDPOINTS.DEVICE.DELETE(deviceId));
    } catch (error) {
        throw ApiError.fromAxiosError(error);
    }
}

/**
 * 기기 아이디 중복 체크
 */
export async function checkDeviceIdApi(deviceId: string): Promise<DeviceIdCheckResponseType> {
    try {
        const response = await axiosInstance.get<DeviceIdCheckResponseType>(
            API_ENDPOINTS.DEVICE.CHECK_ID,
            {
                params: { deviceId }
            }
        );
        return response;
    } catch (error) {
        throw ApiError.fromAxiosError(error);
    }
}

/**
 * 기기 사진 업로드
 */
export async function uploadDevicePhotoApi(formData: FormData): Promise<DevicePhotoResponseType> {
    try {
        const response = await axiosInstance.post<DevicePhotoResponseType>(
            API_ENDPOINTS.DEVICE.UPLOAD_PHOTO,
            formData,
            {
                headers: {
                    'Content-Type': 'multipart/form-data',
                },
            }
        );

        console.log('사진 업로드 응답:', response); // 디버깅용

        // filePath를 getImageUrl로 변환해서 반환
        return {
            ...response,
            filePath: getImageUrl(response.filePath)
        };
    } catch (error) {
        console.error('사진 업로드 실패:', error);
        throw ApiError.fromAxiosError(error);
    }
}

/**
 * 기기 사진 삭제
 */
export async function deleteDevicePhotoApi(photoId: number): Promise<void> {
    try {
        await axiosInstance.delete(API_ENDPOINTS.DEVICE.DELETE_PHOTO(photoId));
    } catch (error) {
        throw ApiError.fromAxiosError(error);
    }
}

/**
 * 기기 통계 조회
 */
export async function getDeviceStatsApi(): Promise<DeviceStatsResponseType> {
    try {
        const response = await axiosInstance.get<DeviceStatsResponseType>(
            API_ENDPOINTS.DEVICE.STATS
        );
        return response;
    } catch (error) {
        throw ApiError.fromAxiosError(error);
    }
}

/**
 * 지역별 기기 목록 조회
 */
/**
 * 지역별 기기 목록 조회
 */
export async function getDevicesByRegionApi(
    regionCode: string,
    page = 0,
    size = 10
): Promise<PageResponse<DeviceResponseType>> {
    try {
        const response = await axiosInstance.get<PageResponse<DeviceResponseType>>(
            API_ENDPOINTS.DEVICE.BY_REGION(regionCode),
            {
                params: {
                    page,
                    size,
                    // installation 정보를 포함하도록 요청
                    includeInstallation: true
                }
            }
        );

        // 응답 데이터 로깅 (디버깅용)
        console.log("지역별 기기 목록 API 응답:", response);

        return response;
    } catch (error) {
        throw ApiError.fromAxiosError(error);
    }
}

/**
 * 엑셀 다운로드
 */
export async function downloadDevicesExcelApi(filters?: {
    regionCode?: string;
    deviceName?: string;
    installationDate?: string;
    status?: string;
}): Promise<void> {
    try {
        const response = await axiosInstance.get(
            API_ENDPOINTS.DEVICE.EXCEL_DOWNLOAD,
            {
                params: filters,
                responseType: 'blob',
            }
        );

        // Blob 응답 처리
        const blob = response as unknown as Blob;

        // 파일 다운로드 처리
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.setAttribute('download', `devices_${new Date().toISOString().split('T')[0]}.xlsx`);
        document.body.appendChild(link);
        link.click();
        link.remove();
        window.URL.revokeObjectURL(url);
    } catch (error) {
        throw ApiError.fromAxiosError(error);
    }
}

/**
 * 엑셀 업로드 (대량 등록)
 */
export async function uploadDevicesExcelApi(file: File): Promise<{
    success: number;
    failed: number;
    errors: Array<{ row: number; message: string }>;
}> {
    try {
        const formData = new FormData();
        formData.append('file', file);

        const response = await axiosInstance.post<{
            success: number;
            failed: number;
            errors: Array<{ row: number; message: string }>;
        }>(
            API_ENDPOINTS.DEVICE.EXCEL_UPLOAD,
            formData,
            {
                headers: {
                    'Content-Type': 'multipart/form-data',
                },
            }
        );
        return response;
    } catch (error) {
        throw ApiError.fromAxiosError(error);
    }
}

/**
 * 지역 목록 조회
 */
export async function getRegionsApi(params?: {
    level?: number;
    parentCode?: string;
    city?: string;
}): Promise<RegionResponseType[]> {
    try {
        const response = await axiosInstance.get<RegionResponseType[]>(
            API_ENDPOINTS.DEVICE.REGIONS,
            { params }
        );
        return response;
    } catch (error) {
        throw ApiError.fromAxiosError(error);
    }
}

/**
 * 지역 코드로 지역 정보 조회
 */
export async function getRegionByCodeApi(code: string): Promise<RegionResponseType> {
    try {
        const response = await axiosInstance.get<RegionResponseType>(
            API_ENDPOINTS.DEVICE.REGION_DETAIL(code)
        );
        return response;
    } catch (error) {
        throw ApiError.fromAxiosError(error);
    }
}

/**
 * 설치 정보만 업데이트
 */
export async function updateDeviceInstallationApi(
    deviceId: number,
    installationData: Partial<DeviceInstallationResponseType>
): Promise<DeviceInstallationResponseType> {
    try {
        const response = await axiosInstance.patch<DeviceInstallationResponseType>(
            API_ENDPOINTS.DEVICE.UPDATE_INSTALLATION(deviceId),
            installationData
        );
        return response;
    } catch (error) {
        throw ApiError.fromAxiosError(error);
    }
}

/**
 * 기기 상태 변경
 */
export async function updateDeviceStatusApi(
    deviceId: number,
    status: 'ACTIVE' | 'INACTIVE' | 'MAINTENANCE'
): Promise<void> {
    try {
        await axiosInstance.patch(
            API_ENDPOINTS.DEVICE.UPDATE_STATUS(deviceId),
            { status }
        );
    } catch (error) {
        throw ApiError.fromAxiosError(error);
    }
}
